import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { Switch } from '@/components/shadcn/switch'
import { useSessionStorage } from '@/hooks'
import { useCurrentOrganisation } from '../useCurrentOrganisation'

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  isFrozen: z.boolean(),
  frequency: z.enum(['Daily', 'Weekly', 'Monthly', 'Annually', 'Ad hoc'], {
    required_error: 'Frequency is required',
  }),
})

export type TOrgProjectForm = z.infer<typeof formSchema>

type MessagesProjectsOrganisationProject = RestSchemas['Messages.Projects.OrganisationProject']
type MessagesProjectsProject = RestSchemas['Messages.Projects.Project']

type OrgProjectFormProps = {
  onSubmit?: () => void
  onProjectCreated?: (projectId: number) => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  organisationId?: number
  selectedOrganisationProject?: MessagesProjectsOrganisationProject
}

function resolveOrganisationId(organisationId?: number) {
  return organisationId != null && Number.isFinite(organisationId) ? organisationId : undefined
}

function resolveSelectedProjectId(project?: MessagesProjectsOrganisationProject) {
  if (!project || project.projectId == null || !Number.isFinite(project.projectId)) {
    return undefined
  }

  return project.projectId
}

function resolveCreatedProjectId(response: MessagesProjectsProject | MessagesProjectsProject[]) {
  const projects = Array.isArray(response) ? response : [response]
  const createdProject = projects.at(-1) ?? projects[0]
  const id = createdProject?.id

  return id != null && Number.isFinite(id) ? id : undefined
}

export const OrgProjectForm = (props: OrgProjectFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  const { onSubmit: onSubmitProp, ...rest } = props
  const onSubmit = useCallback(() => {
    onSubmitProp?.()
    setSheetOpen(false)
  }, [onSubmitProp])

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...rest} onSubmit={onSubmit} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...rest} onSubmit={onSubmit} />
  )
}

const FormComponent = (props: OrgProjectFormProps) => {
  const { organisationId: routeOrganisationId } = useParams()
  const organisationId = resolveOrganisationId(props.organisationId)

  if (organisationId != null) {
    return <FormComponentWithOrgId {...props} orgId={organisationId} />
  }

  if (routeOrganisationId) {
    return <FormComponentFromRoute {...props} />
  }

  return null
}

const FormComponentFromRoute = (props: OrgProjectFormProps) => {
  const { id: orgId } = useCurrentOrganisation()
  return <FormComponentWithOrgId {...props} orgId={orgId} />
}

const FormComponentWithOrgId = (props: OrgProjectFormProps & { orgId: number }) => {
  const { selectedOrganisationProject, onSubmit, onProjectCreated, orgId } = props
  const [sub] = useSessionStorage('sub', 0)
  const selectedProjectId = resolveSelectedProjectId(selectedOrganisationProject)
  const edit = selectedProjectId != null
  const {
    putProjectsApiAddProject,
    postProjectsApiUpdateProject,
    getProjectsApiListOrganisationProjects,
    getProjectsApiGetUserLinkedProjects,
    getSubscriptionsApiGetOrganisations,
  } = useRestClient()

  const { mutate: addProject } = putProjectsApiAddProject.useMutation()
  const { mutate: updateProject } = postProjectsApiUpdateProject.useMutation()

  const invalidateQueries = useCallback(() => {
    getProjectsApiListOrganisationProjects.invalidateQueries({
      parameters: { query: { orgId } },
    })
    getProjectsApiGetUserLinkedProjects.invalidateQueries({})
    getSubscriptionsApiGetOrganisations.invalidateQueries({})
  }, [orgId, getProjectsApiGetUserLinkedProjects, getProjectsApiListOrganisationProjects, getSubscriptionsApiGetOrganisations])

  const defaultValues = useMemo(() => {
    if (edit && selectedOrganisationProject) {
      return {
        name: selectedOrganisationProject.name || '',
        isFrozen: selectedOrganisationProject.isFrozen || false,
        frequency: 'Daily' as const,
      }
    }
    return {
      name: '',
      isFrozen: false,
      frequency: 'Daily' as const,
    }
  }, [edit, selectedOrganisationProject])

  const form = useForm<TOrgProjectForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const onMutationSuccess = useCallback(() => {
    toast.success(edit ? 'Project successfully updated' : 'Project successfully added')
    onSubmit?.()
    form.reset()
    invalidateQueries()
  }, [edit, onSubmit, form, invalidateQueries])

  const onCreateSuccess = useCallback(
    async (response: MessagesProjectsProject | MessagesProjectsProject[]) => {
      const projectId = resolveCreatedProjectId(response)

      onMutationSuccess()

      if (projectId == null) {
        return
      }

      await getProjectsApiGetUserLinkedProjects.fetchQuery({
        parameters: { query: { userId: sub } },
      })
      onProjectCreated?.(projectId)
    },
    [getProjectsApiGetUserLinkedProjects, onMutationSuccess, onProjectCreated, sub]
  )

  const handleSubmit = useCallback(
    async (data: TOrgProjectForm) => {
      if (!orgId) {
        toast.error('No organisation ID found')
        return
      }

      const projectData: MessagesProjectsProject = {
        organisationId: orgId,
        id: selectedProjectId,
        name: data.name,
        isFrozen: data.isFrozen,
        frequency: data.frequency,
      }

      if (edit) {
        updateProject(
          { body: projectData },
          {
            onSuccess: onMutationSuccess,
            onError: (error) => {
              console.error(error)
              toast.error('Failed to update project')
            },
          }
        )
      } else {
        addProject(
          { body: projectData },
          {
            onSuccess: onCreateSuccess,
            onError: (error) => {
              console.error(error)
              toast.error('Failed to add project')
            },
          }
        )
      }
    },
    [orgId, selectedProjectId, edit, addProject, updateProject, onMutationSuccess, onCreateSuccess]
  )

  const frequencyOptions = [
    { value: 'Daily', label: 'Daily' },
    { value: 'Weekly', label: 'Weekly' },
    { value: 'Monthly', label: 'Monthly' },
    { value: 'Annually', label: 'Annually' },
    { value: 'Ad hoc', label: 'Ad hoc' },
  ] as const

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter project name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Frequency</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {frequencyOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isFrozen"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border bg-background px-4 py-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Frozen</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="form-action-container">
              <Button type="submit">{edit ? 'Update Project' : 'Add Project'}</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
